import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /** @type {ResizeObserver | null} */
  #submenuResizeObserver = null;

  /** @type {WeakMap<HTMLElement, number>} */
  #submenuHeightCache = new WeakMap();

  /** @type {number | null} */
  #overflowLinksHeightCache = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(() => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(this.#preloadImages, { timeout: 4000 });
      } else {
        setTimeout(this.#preloadImages, 2000);
      }
    });
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupSubmenuResizeObserver();
  }

  #resizeListener = debounce(() => {
    this.#submenuHeightCache = new WeakMap();
    this.#overflowLinksHeightCache = null;
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /** @type {State} */
  #state = {
    activeItem: null,
  };

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #pointerIdleTimer;

  /** @type {number | null} */
  #pointerRafId = null;

  /** @type {{ x: number, y: number, movementX: number, movementY: number }} */
  #lastPointer = { x: 0, y: 0, movementX: 0, movementY: 0 };

  #onPointerMove = (event) => {
    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;
    this.#lastPointer.movementX = event.movementX;
    this.#lastPointer.movementY = event.movementY;

    if (this.#pointerRafId !== null) return;

    this.#pointerRafId = requestAnimationFrame(() => {
      this.#pointerRafId = null;
      this.#processPointerMove();
    });
  };

  #processPointerMove() {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    const { movementX, movementY } = this.#lastPointer;
    const moving = Math.abs(movementX) >= 1 || Math.abs(movementY) >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  }

  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    const target = document.elementFromPoint(x, y);
    if (!target) return;

    const listItem = target.closest('.menu-list__list-item');
    if (listItem && !listItem.contains(this.#state.activeItem)) {
      listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
    }
  }

  #startPointerTracking(item, previouslyActiveItem) {
    if (previouslyActiveItem) {
      this.#stopPointerTracking(previouslyActiveItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove, { passive: true });
    }
  }

  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);
    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';
    const previouslyActiveItem = this.#state.activeItem;

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      submenu.dataset.active = '';
    }

    this.#cleanupSubmenuResizeObserver();
    this.#applyMenuLayout(submenu, item, isDefaultSlot, hasSubmenu, previouslyActiveItem);
  };

  /**
   * Updates only height CSS variables (used when deferred submenu content loads).
   */
  #updateMenuHeights(submenu, isDefaultSlot, hasSubmenu) {
    const headerComponent = this.headerComponent;
    if (!headerComponent) return;

    const finalHeight = this.#resolveSubmenuHeight(submenu, isDefaultSlot, hasSubmenu);
    const isOverlap = headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
    const headerVisibleHeight =
      isOverlap && headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : headerComponent.offsetHeight;

    const nothingToOpen = finalHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : finalHeight + headerVisibleHeight;

    headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    headerComponent.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Single read pass, then single write pass — avoids layout thrashing.
   */
  #applyMenuLayout(submenu, item, isDefaultSlot, hasSubmenu, previouslyActiveItem) {
    const headerComponent = this.headerComponent;
    if (!headerComponent) return;

    const isOverlap = headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? headerComponent.querySelector('.header__row--top') : headerComponent;

    const finalHeight = this.#resolveSubmenuHeight(submenu, isDefaultSlot, hasSubmenu);
    const headerVisibleHeight =
      isOverlap && headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : headerComponent.offsetHeight;

    const itemRect = item.getBoundingClientRect();
    const boundaryBottom = boundary?.getBoundingClientRect().bottom ?? 0;
    const boxHeight = boundaryBottom - itemRect.top;

    const nothingToOpen = finalHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : finalHeight + headerVisibleHeight;

    headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    headerComponent.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');

    this.#startPointerTracking(item, previouslyActiveItem);
    item.style.setProperty('--box-height', `${boxHeight}px`);

    if (submenu && finalHeight === 0) {
      this.#watchSubmenuResize(submenu, isDefaultSlot, hasSubmenu);
    }
  }

  #resolveSubmenuHeight(submenu, isDefaultSlot, hasSubmenu) {
    if (!submenu) return 0;

    let measured = this.#submenuHeightCache.get(submenu);
    if (measured === undefined) {
      measured = submenu.offsetHeight;
      if (measured > 0) {
        this.#submenuHeightCache.set(submenu, measured);
      }
    }

    if (isDefaultSlot) {
      return measured;
    }

    const overflowListHeight = this.#getOverflowListLinksHeightCached();

    if (hasSubmenu) {
      const overflowHeight = this.overflowMenu?.offsetHeight || 0;
      return Math.max(overflowHeight, overflowListHeight, measured);
    }

    return overflowListHeight;
  }

  #watchSubmenuResize(submenu, isDefaultSlot, hasSubmenu) {
    if (!('ResizeObserver' in window)) return;

    this.#submenuResizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      const height = Math.round(entry?.borderBoxSize?.[0]?.blockSize ?? submenu.offsetHeight);
      if (height <= 0) return;

      this.#submenuHeightCache.set(submenu, height);
      this.#updateMenuHeights(submenu, isDefaultSlot, hasSubmenu);
      this.#cleanupSubmenuResizeObserver();
    });

    this.#submenuResizeObserver.observe(submenu);

    setTimeout(() => this.#cleanupSubmenuResizeObserver(), 500);
  }

  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.headerComponent?.style.setProperty('--full-open-header-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#pointerRafId !== null) {
      cancelAnimationFrame(this.#pointerRafId);
      this.#pointerRafId = null;
    }
    this.#stopPointerTracking(item);
    this.#cleanupSubmenuResizeObserver();

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeightCached() {
    if (this.#overflowLinksHeightCache !== null) {
      return this.#overflowLinksHeightCache;
    }

    const height = this.#getOverflowListLinksHeight();
    this.#overflowLinksHeightCache = height;
    return height;
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks?.length) {
      return this.overflowMenu?.offsetHeight || 0;
    }

    let height = 0;
    for (const link of slottedMenuLinks) {
      if (link instanceof HTMLElement) {
        height = Math.max(height, link.offsetHeight);
      }
    }
    return height;
  }

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupSubmenuResizeObserver() {
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
